import type { JSX, ReactNode, RefObject } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Contrato modal único M457.11: semântica, inert, foco preso, Escape e retorno ao invocador. */
export function ModalSurface({
  title,
  onClose,
  children,
  className = 'modal',
  role = 'dialog',
  testId,
  backdropTestId,
  initialFocusRef,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  role?: 'dialog' | 'alertdialog';
  testId?: string;
  backdropTestId?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
}): JSX.Element | null {
  const titleId = useId();
  const portalRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Em modal empilhado, só a camada ATUAL permanece interativa; portais anteriores também ficam inert.
    const bodyChildren = Array.from(document.body.children).filter((element) => element !== portalRef.current);
    const prior = bodyChildren.map((element) => ({
      element,
      inert: (element as HTMLElement).inert,
      ariaHidden: element.getAttribute('aria-hidden'),
    }));
    bodyChildren.forEach((element) => {
      (element as HTMLElement).inert = true;
      element.setAttribute('aria-hidden', 'true');
    });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    (initialFocusRef?.current ?? modalRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? modalRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(modalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (focusable.length === 0) {
        event.preventDefault();
        modalRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      prior.forEach(({ element, inert, ariaHidden }) => {
        (element as HTMLElement).inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      previousFocus.current?.focus();
    };
  }, [initialFocusRef]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={portalRef}
      className="modal-backdrop"
      data-modal-portal="true"
      data-testid={backdropTestId ?? (testId ? `${testId}-backdrop` : undefined)}
      onPointerDown={(event) => { if (event.target === event.currentTarget) onCloseRef.current(); }}
    >
      <div
        ref={modalRef}
        className={className}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <h2 id={titleId} className="modal-accessible-title">{title}</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
